import {Component} from '@angular/core';
import {FormControl, FormGroup, NgForm, Validators} from '@angular/forms';
import {ComponentBase} from '@drl/core/component-base';
import {IpsTranslateService} from '@drl/core/localization/ips-translate.service';
import {Role} from '@drl/core/store/models/role.model';
import {SystemStoreFacadeService} from '@drl/core/store/system-store-facade.service';
import {NbDialogRef} from '@nebular/theme';
import {TranslateService} from '@ngx-translate/core';
import {CountryISO, SearchCountryField, TooltipLabel} from 'ngx-intl-tel-input';

import {User} from '../../../core/auth/models/user.model';
import {UserFacadeService} from '../../../shared/facades/users-facade.service';
import {RoleType, UserStatus} from '@drl/core/enums';
import {ConfirmationService, SelectItem} from 'primeng/api';
import {AsYouType, PhoneNumber, getCountryCallingCode} from 'libphonenumber-js';
import {UserSessionStoreService as StoreService} from '@drl/core/store/user-session-store.service';
import {Specialty} from '@drl/shared/utility/speciality.enum';
import {
  AppointmentType,
  ApptTypeMap,
} from '@drl/shared/utility/appointment-type.enum';
import {Appointment, Patient} from '@drl/shared/models';
import {PatientFacadeService} from '../../patients/patients-facade.service';
import * as moment from 'moment';

@Component({
  selector: 'app-add-new-record',
  templateUrl: './add-record.component.html',
  styleUrls: [],
})
export class AddNewRecordComponent extends ComponentBase {
  country = CountryISO.India;
  constructor(
    protected dialogRef: NbDialogRef<AddNewRecordComponent>,
    private readonly userFacade: UserFacadeService,
    private readonly systemStoreFacade: SystemStoreFacadeService,
    protected readonly translateSvc: IpsTranslateService,
    private readonly store: StoreService,
    protected readonly patientFacade: PatientFacadeService,
  ) {
    super();
    this.translate = this.translateSvc.translate;
  }
  translate: TranslateService;
  isPhonePristine = true;
  userForm = new FormGroup({
    phone: new FormControl(''),
    extId: new FormControl(''),
  });
  specialists: User[];
  apptToEdit: Appointment;
  selectedApptType: SelectItem;
  defaultCountryISO: CountryISO;
  appt: Appointment;
  apptForm = new FormGroup({
    apptType: new FormControl('', Validators.required),
    name: new FormControl(''),
    doctorName: new FormControl('', Validators.required),
    doctorSpecialty: new FormControl('', Validators.required),
    apptDate: new FormControl(new Date(''), Validators.required),
    description: new FormControl(''),
    apptTime: new FormControl(null, Validators.required),
  });

  separateDialCode = true;
  preferredCountries: CountryISO[] = [
    CountryISO.UnitedStates,
    CountryISO.India,
  ];
  SearchCountryField = SearchCountryField;
  TooltipLabel = TooltipLabel;
  CountryISO = CountryISO;
  searchBy: string = 'phone';
  isEdit = false;
  phoneEditClass() {
    return `${this.isEdit ? 'input-group input-group-edit' : 'input-group'}`;
  }

  user: User;
  users: SelectItem[] = [];
  speciality: SelectItem[] = [];
  apptTypes: SelectItem[] = [];
  invalidPatient: boolean = false;
  minDate = new Date(moment().subtract(1, 'days').toDate());
  maxDate = new Date(moment().add(1, 'years').toDate());
  loading = false;
  patientId: number;
  patientPhoneNumber: string;
  selectedDate: Date;
  selectedTime: string;
  selectedRole: Role;
  uniqEditId: number;
  roles: Role[];
  userLimit = 100;
  defaultVenue = 'Hospital';
  canMobileNumEdit: boolean = true;
  cancelLbl: string;

  private readonly specialities = [
    Specialty.Nutritionist,
    Specialty.Oncologist,
    Specialty.Psychologist,
    Specialty.Others,
  ];

  private readonly appointmentTypes = [
    AppointmentType.Chemotherapy,
    AppointmentType.DoctorVisit,
    AppointmentType.LabVisit,
    AppointmentType.Radiotherapy,
    AppointmentType.Surgery,
    AppointmentType.Oral,
    AppointmentType.Integral,
    AppointmentType.Nuclear,
  ];

  ngOnInit() {
    super.ngOnInit();
    this.loading = true;
    this.getRoles().subscribe(roles => {
      this.roles = roles;
      this.getSpecialists().subscribe(res => {
        this.specialists = res;
        if (this.appt) {
          this.isEdit = true;
          this.setUpEditAppt();
        }
        this.loading = false;
        res.forEach(user => {
          this.users.push({
            label: user.fullName,
            value: user.id,
          });
        });
      }, null);
    });
    this.setCountry();
    this.getAppointmentType();
    this.getSpeciality();
  }

  setUpEditAppt() {
    this.isEdit = true;
    this.canMobileNumEdit = true;
    this.loading = true;
    if (this.appt.searchBy === 'phone') {
      this.searchBy = 'phone';
    } else {
      this.searchBy = 'extId';
    }
    const asYouType = new AsYouType();
    asYouType.input(this.appt.patientMobileNumber);

    this.userForm = new FormGroup({
      phone: new FormControl(
        {
          number: asYouType.getNumber().nationalNumber,
          countryCode: asYouType.getNumber().country || 'US',
        },
        [Validators.required],
      ),
      extId: new FormControl(this.appt.patientExternalId),
    });
    this.setPhone();
    this.uniqEditId = this.appt.uniqId;
    this.patientPhoneNumber = this.appt.patientMobileNumber;
    this.patientId = this.appt.patientId;
    this.apptForm = new FormGroup({
      apptType: new FormControl(this.appt.apptType, Validators.required),
      name: new FormControl(this.appt.patientFullName),
      doctorName: new FormControl(
        Number(this.appt.doctorUserId),
        Validators.required,
      ),
      doctorSpecialty: new FormControl(
        this.appt.doctorSpecialty,
        Validators.required,
      ),
      apptDate: new FormControl(
        new Date(this.appt.apptDate),
        Validators.required,
      ),
      description: new FormControl(this.appt.description),
      apptTime: new FormControl(
        this.appt?.apptTime || null,
        Validators.required,
      ),
    });
    this.loading = false;
    // })
  }

  setCountry() {
    const countryName = this.store.getCountryName();
    this.country = CountryISO[countryName] || CountryISO.Brazil;
  }

  getSpecialists() {
    const specialistRole = this.roles.filter(role => {
      return role.roleType === RoleType.Specialist;
    });
    return this.userFacade.getUsers(
      this.userLimit,
      null,
      null,
      null,
      null,
      null,
      null,
      [specialistRole[0].id],
    );
  }

  findUserByPhoneNumber() {
    this.invalidPatient = false;
    this.user = null;
    this.loading = true;
    const patientRole = this.roles
      .filter(role => {
        return (
          role.roleType === RoleType.Patient ||
          role.roleType === RoleType.Caregiver
        );
      })
      .map(role => role.id);
    this.userFacade
      .getPatient(patientRole, this.userForm.controls['phone'].value.e164Number)
      .subscribe(res => {
        this.loading = false;
        if (res.length === 0) {
          this.invalidPatient = true;
          this.appt = null;
          return;
        }
        this.user = res[0];
        this.patientPhoneNumber = this.user.phone;
        const newAppt = new Appointment();
        newAppt.uniqId = this.appt?.uniqId;
        if (!this.user.isMinor) newAppt.patientFullName = this.user.fullName;
        else newAppt.patientFullName = '';
        this.appt = newAppt;
        this.apptForm = new FormGroup({
          name: new FormControl(this.user.fullName, Validators.required),
          apptType: new FormControl('', Validators.required),
          doctorName: new FormControl('', Validators.required),
          doctorSpecialty: new FormControl('', Validators.required),
          apptDate: new FormControl('', Validators.required),
          description: new FormControl(''),
          apptTime: new FormControl(null, Validators.required),
        });
        this.getPatientDetailsByExternalId(
          this.user.externalId,
          this.user.isMinor,
        );
      });
  }

  findUserByExtId() {
    this.invalidPatient = false;
    this.user = null;
    this.loading = true;
    const patientRole = this.roles.filter(role => {
      return role.roleType === RoleType.Patient;
    });
    this.userFacade
      .getPatient(
        [patientRole[0].id],
        null,
        this.userForm.controls['extId'].value,
      )
      .subscribe(res => {
        this.loading = false;
        if (res.length === 0) {
          this.invalidPatient = true;
          this.appt = null;
          return;
        }
        this.user = res[0];
        this.patientPhoneNumber = this.user.phone;
        const newAppt = new Appointment();
        newAppt.uniqId = this.appt?.uniqId;
        newAppt.patientFullName = this.user.fullName;
        this.appt = newAppt;
        this.apptForm = new FormGroup({
          name: new FormControl(this.user.fullName, Validators.required),
          apptType: new FormControl('', Validators.required),
          doctorName: new FormControl('', Validators.required),
          doctorSpecialty: new FormControl('', Validators.required),
          apptDate: new FormControl('', Validators.required),
          description: new FormControl(''),
          apptTime: new FormControl(null, Validators.required),
        });
        this.getPatientDetailsByExternalId(
          this.user.externalId,
          this.user.isMinor,
        );
      });
  }

  getRoles() {
    return this.systemStoreFacade.getRoles();
  }

  getPatientDetailsByExternalId(id: string, isMinor: Boolean) {
    this.patientFacade.getPatientCGExternalDetails(id).subscribe(res => {
      this.patientId = res.patient.id;
      this.apptForm.patchValue({
        doctorName: res.patient.specialistId,
      });
      if (isMinor) {
        this.patientPhoneNumber = res.careGiver.phone;
        this.appt.patientFullName = `${res.patient.firstName} ${res.patient.lastName}`;
      }
    });
  }

  getSpeciality() {
    this.specialities.forEach(spclty => {
      this.speciality.push({
        label: this.translate.instant(spclty),
        value: spclty,
      });
    });
  }

  getAppointmentType() {
    const apptsTypes = ApptTypeMap(this.translateSvc.translate);
    this.appointmentTypes.forEach(type => {
      this.apptTypes.push({
        label: apptsTypes[type].label,
        value: type,
      });
    });
  }

  saveAppt() {
    if (this.apptForm.invalid) {
      return;
    }
    const doctorUserId = this.apptForm.controls['doctorName'].value;
    this.appt.venue = this.defaultVenue;
    this.appt.apptType = this.apptForm.controls['apptType'].value;
    this.appt.doctorSpecialty = this.apptForm.controls['doctorSpecialty'].value;
    this.appt.description = this.apptForm.controls['description'].value;
    this.appt.apptDate = this.apptForm.controls['apptDate'].value;
    this.appt.apptTime = this.apptForm.controls['apptTime']?.value;
    this.appt.doctorUserId = '' + doctorUserId;
    this.appt.doctorName = this.getSpecialistNameFromId(doctorUserId);
    this.appt.patientFullName = this.appt.patientFullName;
    const [hours, minutes] = this.appt?.apptTime?.split(':');
    this.appt.apptDateTimeString = moment(this.appt.apptDate)
      .set({hour: parseInt(hours) || 0, minute: parseInt(minutes) || 0})
      .format('YYYY-MM-DD HH:mm:ss');
    this.appt.patientMobileNumber = this.patientPhoneNumber;
    this.appt.patientId = this.patientId;
    this.appt.uniqId = Date.now();
    this.appt.patientExternalId =
      this.user?.externalId || this.appt.patientExternalId;
    this.appt.searchBy = this.searchBy;
    if (this.isEdit) {
      this.appt.uniqId = this.uniqEditId;
    }
    this.dialogRef.close({
      error: false,
      appt: this.appt,
    });
  }

  onRadioBtnChange(type: string) {
    this.searchBy = type;
    if (this.userForm.controls['phone'])
      this.userForm.controls['phone'].setValue('');
    if (this.userForm.controls['extId'])
      this.userForm.controls['extId'].setValue('');
    this.appt = null;
  }

  setPhone() {
    if (this.appt.patientMobileNumber) {
      this.userForm.controls.phone.setValue(
        this.asYouTypePhoneManager(this.appt.patientMobileNumber),
      );
    }
  }

  asYouTypeManager(phoneNumber) {
    const asYouType = new AsYouType();
    asYouType.input(`${phoneNumber.includes('+') ? '' : '+'}${phoneNumber}`);

    return new FormGroup({
      phone: new FormControl(
        {
          number: asYouType.getNumber().nationalNumber,
          countryCode: asYouType.getNumber().country || 'US',
          dialCode: getCountryCallingCode(
            asYouType.getNumber().country || 'US',
          ),
        },
        [Validators.required],
      ),
    });
  }

  asYouTypePhoneManager(phoneNumber) {
    const asYouType = new AsYouType();
    asYouType.input(`${phoneNumber.includes('+') ? '' : '+'}${phoneNumber}`);
    const tempDialCode = getCountryCallingCode(
      (asYouType.getNumber() || ({} as PhoneNumber)).country || 'US',
    );
    const dialCode = `${tempDialCode.includes('+') ? '' : '+'}${tempDialCode}`;
    return {
      number:
        (asYouType.getNumber() || ({} as PhoneNumber)).nationalNumber || '',
      countryCode:
        (asYouType.getNumber() || ({} as PhoneNumber)).country || 'US',
      dialCode: dialCode || '',
    };
  }

  getSpecialistNameFromId(id: number) {
    return this.specialists.filter(user => user.id === id)[0].fullName;
  }

  close() {
    this.dialogRef.close();
  }

  onDateChange(event) {
    this.selectedDate = event;
    this.timeSlots = this.getTimeSlots();
    this.apptForm.controls['apptTime']?.reset();
  }

  isToday(date: Date) {
    const inputDate = moment(date); // Convert the input to a Moment object
    return inputDate.isSame(moment(), 'day');
  }
  getTimeSlots() {
    const timeSlots = [];
    let currentDateTime = this.isToday(this.selectedDate)
      ? moment()
      : moment(this.selectedDate);
    currentDateTime = this.roundToNearest5Minutes(currentDateTime);
    let endDateTime = moment(this.selectedDate).endOf('D');
    // Loop until currentTime reaches endTime
    while (
      currentDateTime.isBefore(endDateTime) ||
      currentDateTime.isSame(endDateTime)
    ) {
      // Format time as HH:mm
      const formattedTime = currentDateTime.format('HH:mm');
      timeSlots.push(formattedTime);

      // Add 5 minutes to currentTime
      currentDateTime.add(5, 'minutes');
    }

    return timeSlots;
  }
  roundToNearest5Minutes(time) {
    const minutes = time.minutes();
    const roundedMinutes = Math.round(minutes / 5) * 5;
    return time.clone().minutes(roundedMinutes).seconds(0);
  }
  timeSlots = this.getTimeSlots();
  selectedTimeslot: string;
}
